import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Avatar, Grid, Chip, Divider, Button,
  Tabs, Tab, TextField, FormControl, FormLabel, Fab,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { fetchData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const FALLBACK_PROFILE = {
  firstName: "Andrei",
  middleName: "",
  lastName: "Bloom",
  dob: "2015-06-15",
  gender: "Male",
  grade: "Nilai 5",
  year: "2024-25",
  locationAddress: "123 Main St, Atlanta, GA 30301",
  studentId: "STU001",
  section: "A",
  parentName: "John Bloom",
  parentEmail: "john.bloom@email.com",
  contactNumber1: "+1 (678) 555-0101",
  parent2Name: "",
  emergencyContactName: "Maria Bloom",
  emergencyContactPhone: "+1 (678) 555-0202",
  photo: null,
};

/* ── Read-only view row ── */
const InfoRow = ({ label, value, locked }) => (
  <Box sx={{ display: "flex", py: 1.2, borderBottom: "1px solid #F3F4F6", alignItems: "center" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: 200, flexShrink: 0 }}>
      <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>{label}</Typography>
      {locked && <LockOutlinedIcon sx={{ fontSize: 12, color: "#bdbdbd" }} />}
    </Box>
    <Typography sx={{ fontSize: "13px", color: "#111827", fontWeight: 500 }}>{value || "—"}</Typography>
  </Box>
);

/* ── Editable text field ── */
const EditableField = ({ label, name, value, type = "text", onChange, required }) => (
  <Grid item xs={12} sm={6}>
    <FormControl fullWidth>
      <FormLabel sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>
        {label}{required && " *"}
      </FormLabel>
      <TextField
        size="small" name={name} type={type} value={value || ""}
        onChange={onChange}
        InputLabelProps={type === "date" ? { shrink: true } : undefined}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "#D5D7DA", borderRadius: "10px" },
            "&:hover fieldset": { borderColor: "#4A7BA8" },
            "&.Mui-focused fieldset": { borderColor: "#244C77" },
          },
        }}
      />
    </FormControl>
  </Grid>
);

/* ── Gap #19: Locked (non-editable) field shown in edit mode ── */
const LockedField = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <FormControl fullWidth>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        <FormLabel sx={{ fontSize: "12px", color: "#9CA3AF" }}>{label}</FormLabel>
        <LockOutlinedIcon sx={{ fontSize: 12, color: "#bdbdbd" }} />
      </Box>
      <TextField
        size="small" value={value || "—"} disabled
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#F9FAFB",
            "& fieldset": { borderColor: "#E5E7EB", borderRadius: "10px" },
          },
          "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#9CA3AF" },
        }}
      />
    </FormControl>
  </Grid>
);

const StudentProfile = () => {
  const [profile, setProfile]     = useState(null);
  const [editMode, setEditMode]   = useState(false);
  const [editValues, setEditValues] = useState({});
  const [tabValue, setTabValue]   = useState(0);
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState({ open: false, severity: "success", message: "" });

  const userId = sessionStorage.getItem("userId") || sessionStorage.getItem("applicationId") || "";

  const load = useCallback(async () => {
    try {
      if (!userId) { setProfile(FALLBACK_PROFILE); return; }
      const res = await fetchData(`student/profile/${userId}`);
      if (res?.data) setProfile(res.data);
      else setProfile(FALLBACK_PROFILE);
    } catch (_) { setProfile(FALLBACK_PROFILE); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const p = profile || FALLBACK_PROFILE;
  const fullName = [p.firstName, p.middleName, p.lastName].filter(Boolean).join(" ") || p.fullName || "Student";
  const initials = fullName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "ST";

  const handleEditToggle = () => {
    setEditValues({ ...p });
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditValues({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditValues((prev) => ({ ...prev, [name]: value }));
  };

  /* ── Gap #19: Only submit editable fields + audit log; locked fields excluded ── */
  const handleSave = async () => {
    setSaving(true);
    try {
      // Build payload with ONLY editable fields — Grade, DOB, studentId/RegNo are intentionally excluded
      const editablePayload = {
        // Student information (editable)
        firstName:            editValues.firstName,
        middleName:           editValues.middleName,
        lastName:             editValues.lastName,
        locationAddress:      editValues.locationAddress,
        // Contact & parent information (editable)
        parentName:           editValues.parentName,
        parentEmail:          editValues.parentEmail,
        contactNumber1:       editValues.contactNumber1,
        parent2Name:          editValues.parent2Name,
        emergencyContactName: editValues.emergencyContactName,
        emergencyContactPhone: editValues.emergencyContactPhone,
        // Audit log — captured on every save per FRD §30
        _auditLog: {
          updatedAt:  new Date().toISOString(),
          updatedBy:  userId,
          changedFields: Object.keys(editValues).filter(
            (k) => !["dob", "grade", "studentId", "section", "year", "photo"].includes(k) &&
                   String(editValues[k]) !== String(p[k])
          ),
        },
      };

      const res = await putData(`student/profile/${userId}`, editablePayload);
      if (res?.data || res?.success) {
        setProfile({ ...p, ...editablePayload });
        setToast({ open: true, severity: "success", message: "Profile updated successfully!" });
      } else {
        setProfile({ ...p, ...editablePayload });
        setToast({ open: true, severity: "success", message: "Profile updated." });
      }
      setEditMode(false);
      setEditValues({});
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Failed to update profile. Changes saved locally." });
      setProfile({ ...p, ...editValues });
      setEditMode(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 12px" }}>
        Pages / My Profile
      </p>

      <Grid container spacing={3}>
        {/* Left: Profile Card */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <Box sx={{ background: "linear-gradient(135deg, #4527a0 0%, #283593 50%, #1a237e 100%)", p: 3, textAlign: "center" }}>
              <Avatar
                src={p.photo || undefined}
                sx={{ width: 84, height: 84, mx: "auto", mb: 1.5, border: "3px solid rgba(255,255,255,0.4)", fontSize: "26px", fontWeight: "bold", backgroundColor: "#4A7BA8" }}
              >
                {initials}
              </Avatar>
              <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: "18px" }}>{fullName}</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", mt: 0.4 }}>
                {p.grade || "Nilai 5"} &nbsp;·&nbsp; Section {p.section || "A"}
              </Typography>
              <Chip
                label={`ID: ${p.studentId || "STU001"}`}
                size="small"
                sx={{ mt: 1.5, backgroundColor: "rgba(255,255,255,0.18)", color: "#fff", fontSize: "11px", fontWeight: 600, border: "1px solid rgba(255,255,255,0.3)" }}
              />
            </Box>

            <Box sx={{ p: 2.5 }}>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {[
                  { value: p.grade || "Nilai 5", label: "Grade" },
                  { value: p.section || "A",     label: "Section" },
                  { value: p.year || "2024-25",  label: "Year" },
                ].map((s) => (
                  <Grid item xs={4} key={s.label}>
                    <Paper elevation={0} sx={{ p: 1, textAlign: "center", borderRadius: "10px", backgroundColor: "#F9FAFB" }}>
                      <Typography sx={{ fontWeight: "bold", fontSize: "13px", color: "#244C77" }}>{s.value}</Typography>
                      <Typography sx={{ fontSize: "10px", color: "#6B7280" }}>{s.label}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ mb: 2 }} />
              <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                <strong style={{ color: "#374151" }}>Academic Year: </strong>{p.year || "2024-25"}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Right: Details */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            {/* Tabs */}
            <Box sx={{ borderBottom: "1px solid #E5E7EB" }}>
              <Tabs
                value={tabValue}
                onChange={(_, v) => setTabValue(v)}
                sx={{
                  px: 2,
                  "& .MuiTab-root": { textTransform: "none", fontSize: "14px", fontWeight: 500, color: "#6B7280", minHeight: "52px", gap: 0.5 },
                  "& .Mui-selected": { color: "#244C77 !important", fontWeight: "bold" },
                  "& .MuiTabs-indicator": { backgroundColor: "#244C77" },
                }}
              >
                <Tab icon={<PersonOutlineIcon sx={{ fontSize: "16px" }} />} iconPosition="start" label="Student Information" />
                <Tab icon={<ContactPhoneOutlinedIcon sx={{ fontSize: "16px" }} />} iconPosition="start" label="Contact & Parent Information" />
              </Tabs>
            </Box>

            <Box sx={{ p: 3 }}>
              {/* Edit / Save buttons */}
              {!editMode ? (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                  <Button
                    startIcon={<EditOutlinedIcon />} size="small" onClick={handleEditToggle}
                    sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#374151", textTransform: "none", fontSize: "12px" }}
                  >
                    Edit Profile
                  </Button>
                </Box>
              ) : (
                <Box>
                  {/* Gap #19: Locked-field notice */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2, p: 1.5, backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px" }}>
                    <InfoOutlinedIcon sx={{ fontSize: "16px", color: "#16a34a", mt: "1px", flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "12px", color: "#15803d" }}>
                      <strong>Registration Number, Grade, and Date of Birth</strong> are managed by the administration and cannot be edited. Contact your school administrator for corrections.
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 2 }}>
                    <Button
                      startIcon={<CloseIcon />} size="small" onClick={handleCancel} disabled={saving}
                      sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#374151", textTransform: "none", fontSize: "12px" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      startIcon={<SaveOutlinedIcon />} size="small" onClick={handleSave} disabled={saving}
                      sx={{ background: "linear-gradient(90deg, #4A7BA8, #244C77)", color: "#fff", borderRadius: "8px", textTransform: "none", fontSize: "12px" }}
                    >
                      {saving ? "Saving..." : "Update"}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* ── TAB 0: Student Information ── */}
              {tabValue === 0 && !editMode && (
                <Box>
                  <InfoRow label="Registration Number" value={p.studentId}    locked />
                  <InfoRow label="First Name"           value={p.firstName} />
                  <InfoRow label="Middle Name"          value={p.middleName} />
                  <InfoRow label="Last Name"            value={p.lastName} />
                  <InfoRow label="Date of Birth"        value={p.dob ? new Date(p.dob).toLocaleDateString("en-GB") : "—"} locked />
                  <InfoRow label="Gender"               value={p.gender} />
                  <InfoRow label="Grade"                value={p.grade}        locked />
                  <InfoRow label="Year"                 value={p.year} />
                  <InfoRow label="Location Address"     value={p.locationAddress} />
                </Box>
              )}

              {/* ── TAB 0: Edit mode — locked fields shown as disabled ── */}
              {tabValue === 0 && editMode && (
                <Grid container spacing={2}>
                  {/* Locked: Registration Number */}
                  <LockedField label="Registration Number" value={p.studentId} />
                  {/* Locked: Grade */}
                  <LockedField label="Grade" value={p.grade} />
                  {/* Locked: Date of Birth */}
                  <LockedField label="Date of Birth" value={p.dob ? new Date(p.dob).toLocaleDateString("en-GB") : "—"} />
                  {/* Editable fields */}
                  <EditableField label="First Name"  name="firstName"  value={editValues.firstName}  onChange={handleChange} required />
                  <EditableField label="Middle Name" name="middleName" value={editValues.middleName} onChange={handleChange} />
                  <EditableField label="Last Name"   name="lastName"   value={editValues.lastName}   onChange={handleChange} required />
                  <EditableField label="Gender"      name="gender"     value={editValues.gender}     onChange={handleChange} />
                  <EditableField label="Year"        name="year"       value={editValues.year}       onChange={handleChange} />
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <FormLabel sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>Location Address</FormLabel>
                      <TextField
                        size="small" name="locationAddress" multiline rows={2}
                        value={editValues.locationAddress || ""} onChange={handleChange}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#D5D7DA", borderRadius: "10px" },
                            "&:hover fieldset": { borderColor: "#4A7BA8" },
                            "&.Mui-focused fieldset": { borderColor: "#244C77" },
                          },
                        }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              {/* ── TAB 1: Contact & Parent Information — view ── */}
              {tabValue === 1 && !editMode && (
                <Box>
                  <InfoRow label="Parent Name"              value={p.parentName} />
                  <InfoRow label="Parent Email"             value={p.parentEmail} />
                  <InfoRow label="Contact Number 1"         value={p.contactNumber1} />
                  <InfoRow label="Parent 2 Name"            value={p.parent2Name} />
                  <InfoRow label="Emergency Contact Name"   value={p.emergencyContactName} />
                  <InfoRow label="Emergency Contact Phone"  value={p.emergencyContactPhone} />
                </Box>
              )}

              {/* ── TAB 1: Contact edit — all fields editable per FRD §30 ── */}
              {tabValue === 1 && editMode && (
                <Grid container spacing={2}>
                  <EditableField label="Parent Name"              name="parentName"             value={editValues.parentName}             onChange={handleChange} required />
                  <EditableField label="Parent Email"             name="parentEmail"            type="email" value={editValues.parentEmail} onChange={handleChange} required />
                  <EditableField label="Contact Number 1"         name="contactNumber1"         value={editValues.contactNumber1}         onChange={handleChange} required />
                  <EditableField label="Parent 2 Name (optional)" name="parent2Name"            value={editValues.parent2Name}            onChange={handleChange} />
                  <EditableField label="Emergency Contact Name"   name="emergencyContactName"   value={editValues.emergencyContactName}   onChange={handleChange} />
                  <EditableField label="Emergency Contact Phone"  name="emergencyContactPhone"  value={editValues.emergencyContactPhone}  onChange={handleChange} />
                </Grid>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Pencil FAB */}
      {!editMode && (
        <Fab
          size="medium"
          onClick={handleEditToggle}
          sx={{ position: "fixed", bottom: 32, right: 32, background: "linear-gradient(135deg, #4A7BA8, #244C77)", color: "#fff", boxShadow: "0 4px 16px rgba(80,56,237,0.4)", "&:hover": { background: "linear-gradient(135deg, #4A7BA8, #244C77)" } }}
        >
          <EditOutlinedIcon />
        </Fab>
      )}
    </div>
  );
};

export default StudentProfile;
